<template>
  <div class="global-upload-dialog__wrap">
    <div v-if="showDialog" class="global-upload-dialog">
      <div class="global-upload-dialog__header">
        <span class="global-upload-dialog__header-title">{{ title }}</span>
        <v-btn
          @click="closeDialog"
          class="global-upload-dialog__header-action"
          small
          icon
          ><span class="dash-icon"></span
        ></v-btn>
      </div>
      <div class="global-upload-dialog__content">
        <ul v-if="tasks.length" class="global-upload-dialog__tasks">
          <template v-for="task in tasks">
            <li
              :key="`uploading-task-${task.id}`"
              v-if="task.status === 'COMPLETED'"
              class="global-upload-dialog__task-item global-upload-dialog__task-item--completed"
            >
              <div class="global-upload-dialog__task-item-header">
                <strong class="global-upload-dialog__task-name">{{
                  task.name
                }}</strong>
                <v-icon :size="16" color="accent" class="mr-2"
                  >bhi-check</v-icon
                >
              </div>
            </li>
            <li
              :key="`uploading-task-${task.id}`"
              v-if="task.status === 'PENDING'"
              class="global-upload-dialog__task-item global-upload-dialog__task-item--pending"
            >
              <div class="global-upload-dialog__task-item-header">
                <strong class="global-upload-dialog__task-name">{{
                  task.name
                }}</strong>
                <v-icon :size="16" color="bhGray4" class="mr-2"
                  >bhi-ellipse</v-icon
                >
              </div>
            </li>
            <li
              :key="`uploading-task-${task.id}`"
              v-if="task.status === 'UPLOADING'"
              class="global-upload-dialog__task-item global-upload-dialog__task-item--uploading"
            >
              <div class="global-upload-dialog__task-item-header">
                <strong class="global-upload-dialog__task-name">{{
                  task.name
                }}</strong>
                <v-progress-circular
                  indeterminate
                  :size="16"
                  :width="2"
                  color="accent"
                  class="mr-2"
                >
                  <v-icon :size="16" color="bhGray4">bhi-ellipse</v-icon>
                </v-progress-circular>
              </div>
            </li>
            <li
              :key="`uploading-task-${task.id}`"
              v-if="task.status === 'FAILED'"
              class="global-upload-dialog__task-item global-upload-dialog__task-item--failed"
            >
              <div class="global-upload-dialog__task-item-header">
                <strong class="global-upload-dialog__task-name">{{
                  task.name
                }}</strong>
                <v-icon :size="16" color="bhRed" class="mr-2">bhi-alert</v-icon>
              </div>
              <p class="global-upload-dialog__task-details">{{ task.error }}</p>
            </li>
          </template>
        </ul>
        <div v-else>
          <p class="global-upload-dialog__no-tasks">
            There is no tasks.
          </p>
        </div>
      </div>
    </div>
    <v-btn
      small
      v-if="!showDialog && tasks.length"
      @click="openGlobalUploadDialog"
      class="global-upload-dialog__fab"
      >{{ title }} <strong class="ml-2">—</strong></v-btn
    >
  </div>
</template>

<script>
import emitter from '@/mixins/emitter'
import { MAX_CONCURRENT_UPLOADING_FILES } from '@/helpers/constants'

export default {
  name: 'GlobalUploadDialog',
  components: {},
  mixins: [emitter],
  props: {},
  watch: {
    tasks: {
      handler: function(newVal, oldVal) {
        if (
          this.showDialog === false &&
          newVal.length > 0 &&
          oldVal.length < newVal.length
        ) {
          this.openGlobalUploadDialog()
        }
        this.checkIfCanStartNextTask()
      },
      deep: true,
    },
  },
  data() {
    return {
      showDialog: false,
      tasks: [],
    }
  },
  created() {
    this.registerEvents()
  },
  computed: {
    title() {
      const uploading = this.tasks.filter(el => el.status === 'UPLOADING')
        .length
      const failed = this.tasks.filter(el => el.status === 'FAILED').length
      const completed = this.tasks.filter(el => el.status === 'COMPLETED')
        .length
      const pending = this.tasks.filter(el => el.status === 'PENDING').length

      if (uploading > 0 && pending > 0) {
        return `Uploading ${uploading + pending} items`
      }

      if (uploading > 0) {
        return `Uploading ${uploading} items`
      }

      if (failed > 0) {
        return 'Uploading failed'
      }

      if (completed > 0) {
        return 'Uploading completed'
      }

      return 'Uploading tasks'
    },
  },
  methods: {
    checkIfCanStartNextTask() {
      const uploading = this.tasks.filter(el => el.status === 'UPLOADING')
        .length
      if (uploading >= MAX_CONCURRENT_UPLOADING_FILES) {
        return
      }
      const nextTask = this.tasks.find(el => el.status === 'PENDING')

      this.startUploading(nextTask?.id)
    },
    registerEvents() {
      this.$on(
        'add-uploading-task',
        ({ id, name, taskFunc, taskData, callback = () => {} }) => {
          return this.addTask(id, name, taskFunc, taskData, callback)
        },
      )
      this.$on('fail-uploading-task', ({ id, errorMessage }) => {
        return this.failTask(id, errorMessage)
      })
      this.$on('complete-uploading-task', ({ id }) => {
        return this.completeTask(id)
      })
    },
    openGlobalUploadDialog() {
      this.showDialog = true
    },
    addTask(id, name, taskFunc, taskData, callback) {
      this.tasks = [
        ...this.tasks,
        {
          id,
          taskFunc,
          taskData,
          callback,
          name,
          status: 'PENDING',
        },
      ]
    },
    failTask(id, errorMessage) {
      const tasks = [...this.tasks]
      const taskIndex = tasks.findIndex(el => el.id === id)

      // task not found
      if (taskIndex === -1) {
        return
      }

      // set as failed
      tasks[taskIndex].status = 'FAILED'
      tasks[taskIndex].error = errorMessage
      this.tasks = [...tasks]
    },
    startUploading(id) {
      if (!id) return

      const tasks = [...this.tasks]
      const taskIndex = tasks.findIndex(el => el.id === id)

      // task not found
      if (taskIndex === -1) {
        return
      }

      // set as failed
      tasks[taskIndex].status = 'UPLOADING'
      this.tasks = [...tasks]

      this.upload(tasks[taskIndex])
    },
    async upload(task) {
      if (!task) return
      const { id: taskId, taskFunc, taskData, callback } = task
      try {
        const promise = taskFunc(taskData)
        await promise
        this.completeTask(taskId)
        callback()
      } catch (error) {
        let message = ''
        if (
          typeof error === 'object' &&
          (error?.data?.message || error?.message)
        ) {
          if (error.withoutRetry)
            message = error?.data?.message || error?.message
          else
            message =
              'Please try again! ' + (error?.data?.message || error?.message)
        } else
          message =
            'It seems your Internet connection is unstable. Please try again.'
        this.failTask(taskId, message)
      }
    },
    completeTask(id) {
      const tasks = [...this.tasks]
      const taskIndex = tasks.findIndex(el => el.id === id)

      // task not found
      if (taskIndex === -1) {
        return
      }

      // set as completed
      tasks[taskIndex].status = 'COMPLETED'
      this.tasks = [...tasks]

      // remove completed task after 10 seconds
      setTimeout(() => {
        this.deleteTask(id)
      }, 10000)
    },
    deleteTask(id) {
      this.tasks = this.tasks.filter(el => el.id !== id)
    },
    closeDialog() {
      this.showDialog = false
      let newTasks = this.tasks.filter(el => el.status !== 'FAILED')
      this.tasks = [...newTasks]
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';
.global-upload-dialog {
  margin-left: auto;
  margin-top: auto;
  margin-right: 16px;
  margin-bottom: 16px;
  width: 300px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff;
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2),
    0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);

  .dash-icon {
    display: inline-block;
    width: 16px;
    height: 3px;
    background: currentColor;
  }

  &__wrap {
    position: fixed;
    bottom: 0;
    right: 0;
    z-index: 10;
  }

  &__header {
    background-color: $bh-new-gray;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;

    &-title {
      font-size: 16px;
      line-height: 20px;
    }
  }

  &__tasks {
    margin-left: 0 !important;
    padding-left: 0 !important;
    list-style: none;
    width: 100%;
    max-height: 300px;
    overflow: auto;
  }

  &__no-tasks {
    padding: 8px 12px;
  }

  &__task {
    width: 100%;

    &-details {
      margin-top: 10px;
      margin-bottom: 0 !important;
    }

    &-item {
      padding: 8px 12px;
      line-height: 22px;

      .v-progress-circular,
      .v-icon {
        margin-top: 3px;
      }

      &--completed {
        background-color: $bh-blue--4;
      }
      &--pending {
        .v-icon {
          font-weight: bold;
        }
      }
      &--uploading {
        .v-icon {
          margin-top: 0;
          z-index: -1;
        }
      }
      &--failed {
        color: $bh-red;
        background-color: #fceae9;
      }

      &-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
      }
    }
  }
}
</style>
